import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import LoadingSpinner from './LoadingSpinner';
import PorteurNotification from './ui/PorteurNotification';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';

import CentralHeader from './nosDepenses/CentralHeader';
import CentralStatsCards from './nosDepenses/CentralStatsCards';
import CaissesListSection from './nosDepenses/CaissesListSection';
import CaissesDuJourSection from './nosDepenses/CaissesDuJourSection';
import DemandesDotationSection from './nosDepenses/DemandesDotationSection';
import MouvementsSection from './nosDepenses/MouvementsSection';
import EtatEcoleSection from './nosDepenses/EtatEcoleSection';
import MovementModal from './nosDepenses/modals/MovementModal';
import ObservationModal from './nosDepenses/modals/ObservationModal';
import ClotureMoisModal from './nosDepenses/modals/ClotureMoisModal';
import DecisionDotationModal from './nosDepenses/modals/DecisionDotationModal';
import AutoValidationCaisseDialog from './nosDepenses/modals/AutoValidationCaisseDialog';
import { parseMontant } from './nosDepenses/nosDepensesUtils';

const CaisseCentrale = () => {
  // ============================================================================
  // ÉTATS DE BASE
  // ============================================================================
  const [currentUser, setCurrentUser] = useState(null);
  const [anneeActive, setAnneeActive] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // ============================================================================
  // ÉTATS DES FILTRES
  // ============================================================================
  const [filterPeriode, setFilterPeriode] = useState('mois');
  const [filterStatut, setFilterStatut] = useState('all');
  const [searchGestionnaire, setSearchGestionnaire] = useState('');
  const [expandedCaisses, setExpandedCaisses] = useState(new Set());
  const [selectedTransactions, setSelectedTransactions] = useState(new Set());

  // ============================================================================
  // ÉTATS UI (sections dépliables, comme MaCaisse)
  // ============================================================================
  // L'etat de l'ecole est deplie par defaut : c'est la lecture que le
  // validateur vient chercher, pas une annexe.
  const [expandedSections, setExpandedSections] = useState(new Set(['etat-ecole', 'caisses-du-jour', 'caisses', 'flux']));
  const [caissesDuJour, setCaissesDuJour] = useState([]);
  const [resumeCaissesDuJour, setResumeCaissesDuJour] = useState(null);
  const [chargementCaissesDuJour, setChargementCaissesDuJour] = useState(false);

  const toggleSection = (sectionName) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionName)) newSet.delete(sectionName); else newSet.add(sectionName);
      return newSet;
    });
  };

  // ============================================================================
  // ÉTATS DES MODALS
  // ============================================================================
  const [showObservationModal, setShowObservationModal] = useState(false);
  const [showClotureModal, setShowClotureModal] = useState(false);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [currentObservation, setCurrentObservation] = useState({
    caisseId: null,
    transactionId: null,
    type: 'observation',
    message: ''
  });
  const [movementForm, setMovementForm] = useState({
    type: 'apport',
    montant: '',
    libelle: '',
    description: '',
    beneficiaire_id: '',
    usage_dotation: 'budget_depenses'
  });

  const [decisionContext, setDecisionContext] = useState(null);
  const [decisionComment, setDecisionComment] = useState('');
  const [confirmValiderCaisse, setConfirmValiderCaisse] = useState(null);
  // Validation par la personne concernée (super validateur) :
  // { libelle, raison, longueur, executer(extra), apres(reponse), cle }
  const [autoValidation, setAutoValidation] = useState(null);

  // Clôture mensuelle : données réelles du mois ciblé (endpoints dédiés)
  const [clotureVerification, setClotureVerification] = useState(null);
  const [clotureRecapitulatif, setClotureRecapitulatif] = useState(null);
  const [isCheckingCloture, setIsCheckingCloture] = useState(false);
  const [noteCloture, setNoteCloture] = useState('');

  const [toastInfo, setToastInfoState] = useState(null);

  // ============================================================================
  // DONNÉES RÉELLES
  // ============================================================================
  const [caisses, setCaisses] = useState([]);
  const [mouvements, setMouvements] = useState([]);
  const [employes, setEmployes] = useState([]);
  const currentDate = new Date();
  // La reference du mois consulte par le bilan : l'identifiant d'un mois
  // scolaire, ou `AAAA-MM` pour un mois hors calendrier. Le couple
  // mois/annee laissait choisir 60 combinaisons, dont presque aucune
  // n'existait.
  const [moisBilan, setMoisBilan] = useState('');
  const [moisBilanDisponibles, setMoisBilanDisponibles] = useState({ moisScolaires: [], horsCalendrier: [] });
  const [bilanMensuel, setBilanMensuel] = useState(null);
  // Son propre drapeau : le bilan se recharge a chaque changement de mois,
  // independamment du reste de l'ecran.
  const [chargementBilan, setChargementBilan] = useState(false);
  const [stats, setStats] = useState({
    total_encaissements: 0,
    total_depenses: 0,
    solde_global: 0,
    total_transactions: 0,
    caisses_en_attente: 0,
    caisses_validees: 0,
    caisses_avec_observation: 0,
    total_caisses: 0,
    montant_declare_total: 0,
    ecart_total: 0,
    solde_reel_central: 0,
    entrees_centrale: 0,
    sorties_centrale: 0,
    solde_mouvements_central: 0,
    solde_a_valider: 0
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 1 });
  const [mouvementsPagination, setMouvementsPagination] = useState({ page: 1, limit: 8, total: 0, total_pages: 1 });
  const [demandesDotation, setDemandesDotation] = useState([]);
  const [demandesDotationPagination, setDemandesDotationPagination] = useState({ page: 1, limit: 6, total: 0, total_pages: 1 });
  const [isProcessingDemandeDotation, setIsProcessingDemandeDotation] = useState(null);

  // ============================================================================
  // TOAST
  // ============================================================================
  const showToast = (message, type = 'success', options = {}) => {
    if (!message) return;
    setToastInfoState({ message, type, ...options });
  };
  const hideToast = () => setToastInfoState(null);

  // ============================================================================
  // CHARGEMENT INITIAL
  // ============================================================================
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);
  }, []);

  useEffect(() => {
    const fetchAnneeActive = async () => {
      try {
        const response = await api.anneesScolaires.getActive();
        if (response.success && response.data) {
          setAnneeActive(response.data);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération de l'année scolaire active:", error);
      }
    };
    fetchAnneeActive();
  }, []);

  // ============================================================================
  // FONCTIONS DE CHARGEMENT DES DONNÉES
  // ============================================================================
  const loadCaisses = useCallback(async () => {
    if (!anneeActive) return;
    try {
      setIsLoading(true);
      const params = {
        periode: filterPeriode,
        statut: filterStatut,
        annee_scolaire_id: anneeActive.id,
        search_gestionnaire: searchGestionnaire,
        page: pagination.page,
        limit: pagination.limit
      };

      const response = await api.caisseCentrale.getAll(params);

      if (response.success) {
        const uniqueCaisses = response.data.reduce((acc, caisse) => {
          if (!acc.find((c) => c.id === caisse.id)) acc.push(caisse);
          return acc;
        }, []);
        setCaisses(uniqueCaisses);
        if (response.pagination) setPagination(response.pagination);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur chargement caisses:', error);
      setIsLoading(false);
    }
  }, [anneeActive, filterPeriode, filterStatut, searchGestionnaire, pagination.page, pagination.limit]);

  const loadStats = useCallback(async () => {
    if (!anneeActive) return;
    try {
      setIsLoadingStats(true);
      const response = await api.caisseCentrale.getStats(filterPeriode, anneeActive.id);
      if (response.success) setStats(response.data);
      setIsLoadingStats(false);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
      setIsLoadingStats(false);
    }
  }, [anneeActive, filterPeriode]);

  /**
   * Le mois GREGORIEN de la cloture, deduit du mois consulte.
   *
   * `cloture_mensuelle` archive par mois gregorien, tandis que le bilan suit
   * desormais le calendrier scolaire. Plutot qu'un second selecteur — ou un
   * etat parallele qui divergerait — on derive le couple mois/annee de la
   * borne de debut du mois affiche. La cloture continue donc de suivre ce
   * qu'on regarde, comme avant.
   */
  const bilanPeriod = React.useMemo(() => {
    const debut = bilanMensuel?.periode?.date_debut;
    if (debut) {
      const [annee, mois] = debut.split('-').map(Number);
      if (annee && mois) return { mois, annee };
    }
    return { mois: currentDate.getMonth() + 1, annee: currentDate.getFullYear() };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bilanMensuel?.periode?.date_debut]);

  /**
   * Les mois consultables, servis par le serveur.
   *
   * Le premier mois porteur de mouvements est retenu par defaut : ouvrir sur
   * un mois vide donnait a croire que le bilan ne fonctionnait pas.
   */
  const loadMoisBilan = useCallback(async () => {
    if (!anneeActive) return;
    try {
      const response = await api.caisseCentrale.getMoisBilan(anneeActive.id);
      if (!response.success || !response.data) return;

      setMoisBilanDisponibles(response.data);
      setMoisBilan((actuel) => {
        if (actuel) return actuel;
        const tous = [...(response.data.moisScolaires || []), ...(response.data.horsCalendrier || [])];
        const porteur = tous.find((m) => m.mouvements > 0);
        return String((porteur || tous[tous.length - 1] || {}).reference || '');
      });
    } catch (error) {
      console.error('Erreur chargement des mois du bilan:', error);
    }
  }, [anneeActive]);

  const loadBilanMensuel = useCallback(async () => {
    if (!anneeActive || !moisBilan) return;
    setChargementBilan(true);
    try {
      const response = await api.caisseCentrale.getBilanMensuel({
        annee_scolaire_id: anneeActive.id,
        mois: moisBilan
      });
      if (response.success) setBilanMensuel(response.data);
    } catch (error) {
      console.error('Erreur chargement bilan mensuel:', error);
    } finally {
      setChargementBilan(false);
    }
  }, [anneeActive, moisBilan]);

  const loadEmployes = useCallback(async () => {
    try {
      const response = await api.employes.getAll();
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.employes)
          ? response.employes
          : Array.isArray(response)
            ? response
            : [];
      setEmployes(list);
    } catch (error) {
      console.error('Erreur chargement employes pour dotation:', error);
    }
  }, []);

  const loadMouvements = useCallback(async () => {
    if (!anneeActive) return;
    try {
      const response = await api.caisseCentrale.getMouvements({
        annee_scolaire_id: anneeActive.id,
        page: mouvementsPagination.page,
        limit: mouvementsPagination.limit
      });
      if (response.success) {
        setMouvements(response.data || []);
        if (response.pagination) {
          setMouvementsPagination((prev) => ({
            ...prev,
            total: response.pagination.total,
            total_pages: response.pagination.total_pages
          }));
        }
      }
    } catch (error) {
      console.error('Erreur chargement mouvements centraux:', error);
    }
  }, [anneeActive, mouvementsPagination.page, mouvementsPagination.limit]);

  /**
   * Les caisses du jour, ouvertes comme fermees.
   *
   * Son chargement est independant du reste : une caisse oubliee doit
   * s'afficher meme si le bilan mensuel ou les mouvements echouent.
   */
  const loadCaissesDuJour = useCallback(async () => {
    if (!anneeActive) return;
    setChargementCaissesDuJour(true);
    try {
      const response = await api.caisseCentrale.getCaissesDuJour({
        annee_scolaire_id: anneeActive.id
      });
      if (response.success) {
        setCaissesDuJour(response.data || []);
        setResumeCaissesDuJour(response.resume || null);
      }
    } catch (error) {
      console.error('Erreur chargement des caisses du jour:', error);
    } finally {
      setChargementCaissesDuJour(false);
    }
  }, [anneeActive]);

  const loadDemandesDotation = useCallback(async () => {
    if (!anneeActive) return;
    try {
      const response = await api.caisseCentrale.getDemandesDotation({
        annee_scolaire_id: anneeActive.id,
        page: demandesDotationPagination.page,
        limit: demandesDotationPagination.limit,
        statut: 'all'
      });
      if (response.success) {
        setDemandesDotation(response.data || []);
        if (response.pagination) {
          setDemandesDotationPagination((prev) => ({
            ...prev,
            total: response.pagination.total,
            total_pages: response.pagination.total_pages
          }));
        }
      }
    } catch (error) {
      console.error('Erreur chargement demandes dotation:', error);
    }
  }, [anneeActive, demandesDotationPagination.page, demandesDotationPagination.limit]);

  const reloadAllData = useCallback(async () => {
    await Promise.all([
      loadCaisses(),
      loadStats(),
      loadMoisBilan(),
      loadBilanMensuel(),
      loadMouvements(),
      loadEmployes(),
      loadDemandesDotation(),
      loadCaissesDuJour()
    ]);
  }, [loadCaisses, loadStats, loadMoisBilan, loadBilanMensuel, loadMouvements, loadEmployes, loadDemandesDotation, loadCaissesDuJour]);

  useEffect(() => {
    if (anneeActive) reloadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anneeActive, filterPeriode, filterStatut, searchGestionnaire, pagination.page, moisBilan, reloadAllData]);

  // ============================================================================
  // DONNÉES DÉRIVÉES
  // ============================================================================
  const totalEncaissements = stats.total_encaissements;
  const totalDepenses = stats.total_depenses;
  const soldeReelCentral = stats.solde_reel_central || 0;
  const soldeAValider = stats.solde_a_valider || 0;
  const entreesCentrale = stats.entrees_centrale || 0;
  const sortiesCentrale = stats.sorties_centrale || 0;
  const totalTransactions = stats.total_transactions;
  const caissesEnAttente = stats.caisses_en_attente;
  const caissesValidees = stats.caisses_validees;
  const caissesAvecObservation = stats.caisses_avec_observation;

  const detailMouvements = bilanMensuel?.caisse_centrale?.detail_mouvements || {};
  const remisesValidees = Number(bilanMensuel?.caisse_centrale?.remises_validees || 0);
  const dotationsSorties = Number(detailMouvements.dotations || 0);
  const salairesCentraux = Number(detailMouvements.salaires || 0);
  const grossesDepenses = Number(detailMouvements.grosses_depenses || 0);

  const filteredCaisses = caisses.filter((caisse) => {
    if (filterStatut !== 'all' && caisse.statut !== filterStatut) return false;
    if (searchGestionnaire) {
      const search = searchGestionnaire.toLowerCase();
      const nomComplet = `${caisse.gestionnaire_prenom} ${caisse.gestionnaire_nom}`.toLowerCase();
      if (!nomComplet.includes(search)) return false;
    }
    return true;
  });

  // ============================================================================
  // ACTIONS INTERACTIVES
  // ============================================================================
  const toggleCaisse = (caisseId) => {
    setExpandedCaisses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(caisseId)) newSet.delete(caisseId); else newSet.add(caisseId);
      return newSet;
    });
  };

  const toggleTransaction = (transactionId) => {
    setSelectedTransactions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(transactionId)) newSet.delete(transactionId); else newSet.add(transactionId);
      return newSet;
    });
  };

  const selectAllTransactions = (caisse) => {
    const ids = (caisse.transactions || []).map((t) => t.id);
    setSelectedTransactions((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleValiderCaisse = (caisseId, gestionnaireName) => {
    setConfirmValiderCaisse({ caisseId, gestionnaireName });
  };

  // Séparation des tâches : si la personne est concernée (caisse qu'elle tient
  // ou a clôturée, dotation qu'elle a demandée ou reçoit), le serveur refuse ;
  // s'il s'agit d'un super validateur, il répond AUTO_VALIDATION_REQUISE et on
  // ouvre la fenêtre de justification + code. Renvoie null dans ce cas.
  const executerAvecSeparation = async ({ libelle, executer, apres }) => {
    const response = await executer({});
    if (response?.code === 'AUTO_VALIDATION_REQUISE') {
      setAutoValidation({
        cle: Date.now(),
        libelle,
        raison: response.data?.raison || response.message,
        longueur: response.data?.longueur_justification || 30,
        executer,
        apres
      });
      return null;
    }
    return response;
  };

  const confirmerAutoValidation = async (extra) => {
    if (!autoValidation) return null;
    const response = await autoValidation.executer(extra);
    if (response?.success) {
      const { apres } = autoValidation;
      setAutoValidation(null);
      await apres(response);
    }
    return response;
  };

  const confirmerValiderCaisse = async () => {
    if (!confirmValiderCaisse) return;
    const { caisseId, gestionnaireName } = confirmValiderCaisse;
    const apres = async (reponse) => {
      showToast(reponse?.data?.auto_validee ? 'Caisse validée — auto-validation journalisée.' : 'Caisse validée avec succès.', 'success');
      await reloadAllData();
    };
    try {
      setIsLoading(true);
      const response = await executerAvecSeparation({
        libelle: `Valider la caisse de ${gestionnaireName}`,
        executer: (extra) => api.caisseCentrale.validerCaisse(caisseId, null, extra),
        apres
      });
      if (!response) {
        // Fenêtre d'auto-validation ouverte.
      } else if (response.success) {
        await apres(response);
      } else {
        showToast(response.message || 'Erreur inconnue lors de la validation.', 'error');
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur validation caisse:', error);
      showToast('Erreur lors de la validation.', 'error');
      setIsLoading(false);
    } finally {
      setConfirmValiderCaisse(null);
    }
  };

  const handleValiderTransaction = async (caisseId, transactionId) => {
    const apres = async () => {
      showToast('Transaction validée.', 'success');
      setSelectedTransactions((prev) => {
        const next = new Set(prev);
        next.delete(transactionId);
        return next;
      });
      await reloadAllData();
    };
    try {
      setIsLoading(true);
      const response = await executerAvecSeparation({
        libelle: 'Valider une transaction de la caisse',
        executer: (extra) => api.caisseCentrale.validerTransaction(caisseId, transactionId, extra),
        apres
      });
      if (!response) {
        // Fenêtre d'auto-validation ouverte.
      } else if (response.success) {
        await apres(response);
      } else {
        showToast(response.message || 'Erreur inconnue lors de la validation.', 'error');
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur validation transaction:', error);
      showToast('Erreur lors de la validation.', 'error');
      setIsLoading(false);
    }
  };

  const handleValiderSelection = async (caisse) => {
    const ids = (caisse.transactions || []).filter((t) => selectedTransactions.has(t.id)).map((t) => t.id);
    if (ids.length === 0) return;
    // Un seul appel pour toute la sélection : un code de double
    // authentification ne sert qu'une fois, et la sélection passe ou non en bloc.
    const apres = async () => {
      showToast(`${ids.length} transaction(s) validée(s).`, 'success');
      setSelectedTransactions((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      await reloadAllData();
    };
    try {
      setIsLoading(true);
      const response = await executerAvecSeparation({
        libelle: `Valider ${ids.length} transaction(s) de la caisse`,
        executer: (extra) => api.caisseCentrale.validerTransaction(caisse.id, ids, extra),
        apres
      });
      if (response?.success) {
        await apres(response);
      } else if (response) {
        showToast(response.message || 'Erreur lors de la validation de la sélection.', 'error');
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur validation sélection:', error);
      showToast('Erreur lors de la validation de la sélection.', 'error');
      setIsLoading(false);
    }
  };

  const handleSignalerTransaction = (caisseId, transactionId, type = 'observation') => {
    setCurrentObservation({ caisseId, transactionId, type, message: '' });
    setShowObservationModal(true);
  };

  const handleAjouterObservation = async () => {
    if (!currentObservation.message.trim()) {
      showToast('Veuillez saisir un message.', 'error');
      return;
    }
    try {
      setIsLoading(true);
      const response = await api.caisseCentrale.ajouterObservation(currentObservation.caisseId, {
        transaction_id: currentObservation.transactionId,
        type: currentObservation.type,
        message: currentObservation.message
      });
      if (response.success) {
        showToast('Observation envoyée avec succès.', 'success');
        setShowObservationModal(false);
        setCurrentObservation({ caisseId: null, transactionId: null, type: 'observation', message: '' });
        await reloadAllData();
      } else {
        showToast(response.message || 'Erreur inconnue.', 'error');
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur ajout observation:', error);
      showToast("Erreur lors de l'ajout de l'observation.", 'error');
      setIsLoading(false);
    }
  };

  // Ouverture de la clôture : on interroge les endpoints dédiés, scopés sur le mois
  // sélectionné dans le bilan mensuel (et non sur le mois calendaire courant).
  const handleOpenClotureModal = async () => {
    setShowClotureModal(true);
    setClotureVerification(null);
    setClotureRecapitulatif(null);
    setNoteCloture('');

    try {
      setIsCheckingCloture(true);
      const [verifRes, recapRes] = await Promise.all([
        api.caisseCentrale.verifierClotureMensuelle(bilanPeriod.mois, bilanPeriod.annee, anneeActive?.id),
        api.caisseCentrale.getRecapitulatifMensuel(bilanPeriod.mois, bilanPeriod.annee, anneeActive?.id)
      ]);

      if (verifRes.success) setClotureVerification(verifRes.data);
      else showToast(verifRes.message || 'Erreur lors de la vérification du mois.', 'error');

      if (recapRes.success) setClotureRecapitulatif(recapRes.data);
    } catch (error) {
      console.error('Erreur vérification clôture mensuelle:', error);
      showToast('Erreur lors de la vérification du mois.', 'error');
    } finally {
      setIsCheckingCloture(false);
    }
  };

  const handleCloturerMois = async () => {
    try {
      setIsLoading(true);
      const response = await api.caisseCentrale.cloturerMois(
        bilanPeriod.mois,
        bilanPeriod.annee,
        anneeActive?.id,
        noteCloture.trim() || null
      );
      if (response.success) {
        showToast('Mois clôturé avec succès. L\'archive comptable a été générée.', 'success');
        setShowClotureModal(false);
        setNoteCloture('');
        await reloadAllData();
      } else {
        showToast(response.message || 'Erreur inconnue.', 'error');
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur clôture mois:', error);
      showToast('Erreur lors de la clôture du mois.', 'error');
      setIsLoading(false);
    }
  };

  const handleCreateCentralMovement = async () => {
    const montant = parseMontant(movementForm.montant);

    if (!movementForm.type) {
      showToast('Veuillez choisir un type de mouvement.', 'error');
      return;
    }
    if (montant <= 0) {
      showToast('Le montant doit être supérieur à 0.', 'error');
      return;
    }
    if (movementForm.type === 'dotation' && !movementForm.beneficiaire_id) {
      showToast('Veuillez choisir la caisse/personne bénéficiaire.', 'error');
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.caisseCentrale.createMouvement({
        type: movementForm.type,
        montant,
        libelle: movementForm.libelle,
        description: movementForm.description,
        annee_scolaire_id: anneeActive?.id,
        beneficiaire_id: movementForm.type === 'dotation' ? movementForm.beneficiaire_id : null,
        usage_dotation: movementForm.type === 'dotation' ? movementForm.usage_dotation : null
      });

      if (response.success) {
        showToast('Mouvement central enregistré avec succès.', 'success');
        setShowMovementModal(false);
        setMovementForm({ type: 'apport', montant: '', libelle: '', description: '', beneficiaire_id: '', usage_dotation: 'budget_depenses' });
        await reloadAllData();
      } else {
        showToast(response.message || 'Erreur inconnue.', 'error');
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur création mouvement central:', error);
      showToast(error.message || 'Erreur lors de la création du mouvement central.', 'error');
      setIsLoading(false);
    }
  };

  const openDecisionDotation = (demande, decision) => {
    setDecisionContext({ demande, decision });
    setDecisionComment('');
  };

  const handleDecisionDemandeDotation = async () => {
    if (!decisionContext) return;
    const { demande, decision } = decisionContext;

    if (decision === 'refusee' && !decisionComment.trim()) {
      showToast('Le motif du refus est obligatoire.', 'error');
      return;
    }

    const commentaire = decisionComment;
    const apres = async (reponse) => {
      showToast(reponse?.message || 'Demande de dotation traitée.', 'success');
      setDecisionContext(null);
      setDecisionComment('');
      await reloadAllData();
    };

    try {
      setIsProcessingDemandeDotation(demande.id);
      const response = await executerAvecSeparation({
        libelle: `Accepter la demande de dotation n° ${demande.id}`,
        executer: (extra) => api.caisseCentrale.deciderDemandeDotation(demande.id, {
          decision,
          commentaire_decision: commentaire,
          ...extra
        }),
        apres
      });

      if (!response) {
        // Une fenêtre à la fois : la décision laisse place à l'auto-validation.
        setDecisionContext(null);
      } else if (response.success) {
        await apres(response);
      } else {
        showToast(response.message || 'Erreur lors du traitement de la demande.', 'error');
      }
    } catch (error) {
      console.error('Erreur traitement demande dotation:', error);
      showToast(error.message || 'Erreur lors du traitement de la demande.', 'error');
    } finally {
      setIsProcessingDemandeDotation(null);
    }
  };

  const handleExporter = () => {
    let csv = 'Gestionnaire,Statut,Date clôture,Encaissements,Dépenses,Solde,Remis,Écart,Transactions\n';
    filteredCaisses.forEach((c) => {
      const gestionnaire = `${c.gestionnaire_prenom || ''} ${c.gestionnaire_nom || ''}`.trim();
      csv += `${gestionnaire},${c.statut},${c.date_cloture_caisse},${c.total_encaissements},${c.total_depenses},${c.solde},${c.montant_declare ?? c.solde},${c.ecart || 0},${c.nb_transactions}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `caisse_centrale_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // ============================================================================
  // RENDU
  // ============================================================================
  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full space-y-6">

        <CentralHeader
          currentUser={currentUser}
          filterPeriode={filterPeriode}
          setFilterPeriode={setFilterPeriode}
          onNouveauMouvement={() => setShowMovementModal(true)}
          onCloturerMois={handleOpenClotureModal}
          onExporter={handleExporter}
          clotureDisabled={false}
          caissesEnAttente={caissesEnAttente}
          caissesAvecObservation={caissesAvecObservation}
          caissesValidees={caissesValidees}
        />

        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-white">
              <LoadingSpinner />
            </div>
          )}

          <CentralStatsCards
            soldeReelCentral={soldeReelCentral}
            entreesCentrale={entreesCentrale}
            sortiesCentrale={sortiesCentrale}
            soldeAValider={soldeAValider}
            totalTransactions={totalTransactions}
            totalEncaissements={totalEncaissements}
            totalDepenses={totalDepenses}
            caissesEnAttente={caissesEnAttente}
            caissesValidees={caissesValidees}
            caissesAvecObservation={caissesAvecObservation}
            remisesValidees={remisesValidees}
            dotationsSorties={dotationsSorties}
            salairesCentraux={salairesCentraux}
            grossesDepenses={grossesDepenses}
            isLoadingStats={isLoadingStats}
          />
        </div>

        <CaissesDuJourSection
          isExpanded={expandedSections.has('caisses-du-jour')}
          onToggleSection={() => toggleSection('caisses-du-jour')}
          isLoading={chargementCaissesDuJour}
          lignes={caissesDuJour}
          resume={resumeCaissesDuJour}
          onRafraichir={loadCaissesDuJour}
        />

        <CaissesListSection
          isExpanded={expandedSections.has('caisses')}
          onToggleSection={() => toggleSection('caisses')}
          isLoading={isLoading}
          caisses={caisses}
          filteredCaisses={filteredCaisses}
          expandedCaisses={expandedCaisses}
          toggleCaisse={toggleCaisse}
          selectedTransactions={selectedTransactions}
          toggleTransaction={toggleTransaction}
          selectAllTransactions={selectAllTransactions}
          onValiderCaisse={handleValiderCaisse}
          onObservationGlobale={(caisseId) => {
            setCurrentObservation({ caisseId, transactionId: null, type: 'observation', message: '' });
            setShowObservationModal(true);
          }}
          onValiderTransaction={handleValiderTransaction}
          onSignalerTransaction={handleSignalerTransaction}
          onValiderSelection={handleValiderSelection}
          pagination={pagination}
          setPagination={setPagination}
          filterStatut={filterStatut}
          setFilterStatut={setFilterStatut}
          searchGestionnaire={searchGestionnaire}
          setSearchGestionnaire={setSearchGestionnaire}
        />

        {/* Flux financiers centraux -- section dépliable contenant deux sous-sections */}
        <div className="px-6">
          <Card>
            <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => toggleSection('flux')}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {expandedSections.has('flux') ? (
                    <ChevronDown size={24} className="text-gray-600" />
                  ) : (
                    <ChevronRight size={24} className="text-gray-600" />
                  )}
                  <div>
                    <CardTitle className="text-xl text-orange-600 flex items-center gap-2">
                      <TrendingUp size={24} />
                      Flux financiers centraux
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      dotations demandées et registre des mouvements de la caisse centrale
                    </p>
                  </div>
                </div>
              </div>
            </CardHeader>

            {expandedSections.has('flux') && (
              <CardContent className="pt-6">
                <DemandesDotationSection
                  demandesDotation={demandesDotation}
                  pagination={demandesDotationPagination}
                  setPagination={setDemandesDotationPagination}
                  isProcessingDemandeDotation={isProcessingDemandeDotation}
                  onValider={(demande) => openDecisionDotation(demande, 'validee')}
                  onRefuser={(demande) => openDecisionDotation(demande, 'refusee')}
                />

                <MouvementsSection
                  mouvements={mouvements}
                  pagination={mouvementsPagination}
                  setPagination={setMouvementsPagination}
                />
              </CardContent>
            )}
          </Card>
        </div>

        {/* L'etat de l'ecole par classe : le validateur lit tout d'un seul
            endroit, sans se connecter sur le compte de chaque gestionnaire. */}
        <EtatEcoleSection
          isExpanded={expandedSections.has('etat-ecole')}
          onToggleSection={() => toggleSection('etat-ecole')}
          moisBilan={moisBilan}
          setMoisBilan={setMoisBilan}
          moisDisponibles={moisBilanDisponibles}
          bilanMensuel={bilanMensuel}
          chargement={chargementBilan}
        />



      </div>

      {/* ==================================================================== MODALES ==================================================================== */}
      <MovementModal
        isOpen={showMovementModal}
        onClose={() => setShowMovementModal(false)}
        form={movementForm}
        setForm={setMovementForm}
        employes={employes}
        isLoading={isLoading}
        onSubmit={handleCreateCentralMovement}
        soldeReelCentral={soldeReelCentral}
      />

      <ObservationModal
        isOpen={showObservationModal}
        onClose={() => {
          setShowObservationModal(false);
          setCurrentObservation({ caisseId: null, transactionId: null, type: 'observation', message: '' });
        }}
        observation={currentObservation}
        setObservation={setCurrentObservation}
        isLoading={isLoading}
        onSubmit={handleAjouterObservation}
      />

      <ClotureMoisModal
        isOpen={showClotureModal}
        onClose={() => setShowClotureModal(false)}
        mois={bilanPeriod.mois}
        annee={bilanPeriod.annee}
        verification={clotureVerification}
        recapitulatif={clotureRecapitulatif}
        isChecking={isCheckingCloture}
        noteCloture={noteCloture}
        setNoteCloture={setNoteCloture}
        isLoading={isLoading}
        onSubmit={handleCloturerMois}
      />

      <DecisionDotationModal
        isOpen={!!decisionContext}
        onClose={() => { setDecisionContext(null); setDecisionComment(''); }}
        demande={decisionContext?.demande}
        decision={decisionContext?.decision}
        comment={decisionComment}
        setComment={setDecisionComment}
        isProcessing={!!isProcessingDemandeDotation}
        onSubmit={handleDecisionDemandeDotation}
      />

      <AutoValidationCaisseDialog
        key={autoValidation?.cle || 'aucune'}
        open={Boolean(autoValidation)}
        libelle={autoValidation?.libelle}
        raison={autoValidation?.raison}
        longueurJustification={autoValidation?.longueur}
        onClose={() => setAutoValidation(null)}
        onConfirmer={confirmerAutoValidation}
      />

      <ConfirmDialog
        isOpen={!!confirmValiderCaisse}
        onClose={() => setConfirmValiderCaisse(null)}
        onConfirm={confirmerValiderCaisse}
        title="Valider la caisse"
        description={confirmValiderCaisse ? `Voulez-vous vraiment valider toutes les transactions de ${confirmValiderCaisse.gestionnaireName} ?` : ''}
        confirmText="Valider"
        cancelText="Annuler"
        type="question"
        isLoading={isLoading}
      />

      {/* `PorteurNotification` rend le toast dans `ToastPortal`.
          Rendu directement ici, il s'affichait SOUS l'assombrissement d'un
          `Dialog` : un message d'erreur déclenché depuis « Clôturer le mois »
          était invisible. L'arbitrage court/long reste le même. */}
      <PorteurNotification notification={toastInfo} onFermer={hideToast} />
    </div>
  );
};

export default CaisseCentrale;
